import clsx from "clsx";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useApplication } from "@ryde/redux";
import {
  APP_VERSION,
  buildTimestampAtom,
  ControlsApi,
  isValidDateTime,
  Log,
  mobileSettingsSearchAtom,
  settingsDrawerOpenStateAtom,
  useFormats,
  useHasPermissionsCheck,
  useIsMobile,
} from "@ryde/shared";

import { BaseDrawer, DrawerContentStyled, DrawerHeader } from "../drawers";
import {
  filterSettingData,
  useSettingsData,
  useSettingsDataAdmin,
} from "./data";
import { MobileLangSwitch } from "./mobile";
import { MobileSettingsSearch } from "./mobile/mobile-settings-search";
import { SettingsGroup } from "./settings.group";
import {
  SettingsContainerStyled,
  SettingsEnterContentStyled,
} from "./settings.styled";

export const Settings: FC = () => {
  const { t } = useTranslation();
  const setSettingDrawerOpenState = useSetRecoilState(
    settingsDrawerOpenStateAtom,
  );
  const { isAdminApp } = useApplication();
  const { isMobile } = useIsMobile();
  const buildTimeStamp = useRecoilValue(buildTimestampAtom);
  const [apiVersion, setApiVersion] = useState<string>("");

  const settingsSearchText = useRecoilValue(mobileSettingsSearchAtom);
  const { settingsDataAdmin } = useSettingsDataAdmin();
  const { settingsItems } = useSettingsData();
  const { formatDateTimeNormalized } = useFormats();
  const { hasPermissionsCheck } = useHasPermissionsCheck();

  const settingsData = useMemo(
    () => (isAdminApp ? settingsDataAdmin : settingsItems),
    [settingsItems, settingsDataAdmin, isAdminApp],
  );

  const settingsDataWithAccess = useMemo(
    () =>
      settingsData.filter(group =>
        group.groupItems.some(
          item =>
            item.skipPermissionCheck ||
            hasPermissionsCheck({
              entity: item.permission?.entity,
              action: item.permission?.action,
            }),
        ),
      ),
    [settingsData, hasPermissionsCheck],
  );

  const settingsDataFiltered = useMemo(() => {
    if (!settingsSearchText?.trim()) {
      return settingsDataWithAccess;
    }

    return filterSettingData(settingsDataWithAccess, settingsSearchText);
  }, [settingsSearchText, settingsDataWithAccess]);

  const onRightBarCloseClick = (): void => {
    setSettingDrawerOpenState(false);
  };

  useEffect(() => {
    const fetchApiVersion = async (): Promise<void> => {
      try {
        const version = await ControlsApi.getApiDevVersion();
        setApiVersion(version?.version || "");
      } catch (error) {
        Log.warn("Failed to fetch API version", error);
        setApiVersion("");
      }
    };

    fetchApiVersion();
  }, []);

  const versionDate = useMemo(() => {
    const baseVersion = `v${APP_VERSION}`;
    const fullVersion = apiVersion
      ? `${baseVersion}_${apiVersion}`
      : baseVersion;

    if (isValidDateTime(buildTimeStamp)) {
      return `${fullVersion} (${t("notification.gridLabel.updatedAt")} ${formatDateTimeNormalized(buildTimeStamp)})`;
    }

    return fullVersion;
  }, [buildTimeStamp, formatDateTimeNormalized, t, apiVersion]);

  return (
    <BaseDrawer skipCssEnter onCloseDrawer={onRightBarCloseClick}>
      <DrawerHeader
        title={t(`general.SETTINGS`)}
        subTitle={versionDate}
        onCloseDrawer={onRightBarCloseClick}
      />
      <DrawerContentStyled
        className={clsx({
          "drawer-form-with-image": true,
          "settings-drawer__container": true,
          isAdmin: isAdminApp,
        })}
      >
        <SettingsEnterContentStyled>
          <div className="settings-search">
            <MobileSettingsSearch />
          </div>

          <SettingsContainerStyled>
            {settingsDataFiltered.map(item => (
              <SettingsGroup key={item.groupTitle} {...item}></SettingsGroup>
            ))}
            {isMobile && <MobileLangSwitch />}
          </SettingsContainerStyled>
        </SettingsEnterContentStyled>
      </DrawerContentStyled>
    </BaseDrawer>
  );
};
